import React from "react";
import clsx from "clsx";
import { withStyles } from "@material-ui/core";
import { AutoSizer, Column, Table } from "react-virtualized";
import { TableRow } from "@material-ui/core";
import { Cell, InputCell, HeadCell, IColumn } from "../Cells";
import TableResizer from "./TableResizer";
import ColumnsCustomScroll from "./ColumnsCustomScroll";
import { tableStyles } from "../styles";

interface IProps {
  classes: any;
  /** Propriedades das colunas da Esquerda. */
  columnsLeft: Array<IColumn>;
  /** Propriedades das colunas da Direita. */
  columnsRight: Array<IColumn>;
  /** Total de linhas que a tabela terá. */
  rowCount: number;
  /** Função que será chamada ao obter uma nova linha para ser gerada. */
  rowGetter: Function;
  /** Altura da linha. */
  rowHeight?: number;
  /** Habilita/desabilita cor de destaque da linha ao passar o mouse sobre a mesma. */
  rowHover?: boolean;
  /** Altura do cabeçalho da tabela. */
  headerHeight?: number;
  /** Função que será executada ao clicar em uma linha. */
  onRowClick?: Function;

  /** Classe personalizada para as células do cabeçalho da tabela. */
  headCellClassName?: string;
  /** Classe personalizada para as células do corpo da tabela. */
  cellClassName?: string;
  /** Desabilita alterações no input da célula. */
  disableInput?: boolean;
  /** Função que será executada ao alterar o valor da célula e que deverá retornar o valor a ser apresentado no input.
   * Está função permite a converção de dados, como por exemplo, de texto para números. */
  onChangeInputCell?: (
    rowIndex: number | undefined,
    dataKey: string,
    value
  ) => any;
  /** Função que será executada ao clicar na célula o que habilitará o input de texto. */
  onEnableInputCell?: (rowIndex: number | undefined, dataKey: string) => any;
  /** Função que será executada ao alterar o valor do input, passando o novo valor digitado pelo usuário.
   * Por questão de performance, ficou definido que está função só será executada quando o usuário confirmar a alteração feita no input, ou seja, somente quando ele sair do campo ou apertar "Enter". */
  onChangeInputCellValue: (
    rowIndex: number | undefined,
    dataKey: string,
    value
  ) => void;
}

class MuiVirtualizedMultiTable extends React.PureComponent<
  IProps,
  { leftWidth: number; rightWidth: number; scrollTop: number }
> {
  private columnLeftWidth = 0;
  private columnRightWidth = 0;

  private columnsLeftToRender: IColumn[];
  private columnsRightToRender: IColumn[];

  static defaultProps = {
    headerHeight: 48,
    rowHover: true,
    rowHeight: 48,
  };

  constructor(props) {
    super(props);

    this.columnsLeftToRender = this.getColumnsSplitted(props.columnsLeft);
    this.columnsRightToRender = this.getColumnsSplitted(props.columnsRight);
    this.columnLeftWidth = this.getColumnsWidth(props.columnsLeft);
    this.columnRightWidth = this.getColumnsWidth(props.columnsRight);

    this.state = {
      leftWidth: 0,
      rightWidth: 0,
      scrollTop: 0,
    };
  }

  getColumnsWidth = (columns) => {
    return columns.reduce((sum: number, column) => sum + column.width, 0);
  };

  getColumnsSplitted = (columns) => {
    return columns.reduce((newArray, column) => {
      if (column.subColumns) {
        column.subColumns.forEach((clmn) => {
          newArray.push(clmn);
        });
      } else {
        newArray.push(column);
      }

      return newArray;
    }, []);
  };

  getRowClassName = ({ index }) => {
    const { classes, rowHover } = this.props;

    return clsx(classes.tableRow, classes.flexContainer, {
      [classes.tableRowHover]: index !== -1 && rowHover,
    });
  };

  cellRenderer = ({ dataKey, width, columnData, rowData, ...props }) => {
    const {
      classes,
      rowHeight,
      cellClassName,
      disableInput,
      onChangeInputCell,
      onEnableInputCell,
      onChangeInputCellValue,
    } = this.props;

    return (
      <Cell
        dataKey={dataKey}
        className={classes.tableCellMain}
        rowHeight={rowHeight || 0}
      >
        <TableRow
          className={`MuiVirtualizedTable-row-${props.rowIndex}`}
          style={{ width }}
          component="div"
        >
          {columnData.map((column, index) => {
            const cellProps = {
              ...props,
              key: `body-${dataKey}-${column.dataKey}`,
              dataKey: column.dataKey,
              columnIndex: index,
              columnData: column,
              rowData: rowData,
              className: cellClassName,
              rowHeight: rowHeight || 0,
              fixedSize: true,
            };
            return !column.input ? (
              <Cell {...cellProps}>{rowData[column.dataKey]}</Cell>
            ) : (
              <InputCell
                {...cellProps}
                value={rowData[column.dataKey]}
                onChange={onChangeInputCell}
                onEnableInput={onEnableInputCell}
                onChangeInputValue={onChangeInputCellValue}
                disableInput={Boolean(disableInput)}
              />
            );
          })}
        </TableRow>
      </Cell>
    );
  };

  headerRenderer = ({
    dataKey,
    width,
    columnData,
    maxWidth = 0,
    minWidth = 0,
    position = 0,
  }) => {
    const { headerHeight, classes, headCellClassName } = this.props;

    return (
      <>
        {dataKey === "right" && (
          <TableResizer
            initialPosition={position}
            maxWidth={maxWidth}
            minWidth={minWidth}
            onMouseUp={(leftWidth, rightWidth) => {
              this.setState({
                leftWidth,
                rightWidth,
              });
            }}
          />
        )}
        <HeadCell
          className={clsx(
            classes.tableHeadCellMain,
            `MuiVirtualizedTable-header-${dataKey}`
          )}
          headerHeight={headerHeight || 0}
        >
          <TableRow style={{ width }} component="div">
            {columnData.map((column, index) => (
              <HeadCell
                key={`head-${dataKey}-${column.dataKey}`}
                columnData={column}
                className={headCellClassName}
                headerHeight={headerHeight || 0}
                fixedSize={true}
              >
                {column.label}
              </HeadCell>
            ))}
          </TableRow>
        </HeadCell>
      </>
    );
  };

  render() {
    const { classes, columnsLeft, columnsRight, ...tableProps } = this.props;
    const { leftWidth, rightWidth } = this.state;

    return (
      <AutoSizer>
        {({ height, width }) => {
          const minWidth = Math.max(0, width - this.columnRightWidth);
          const maxWidth = width;
          const middle = width / 2;

          return (
            <>
              <Table
                height={height}
                width={width}
                gridStyle={{
                  direction: "inherit",
                }}
                className={clsx(
                  classes.table,
                  classes.autoSizedTable,
                  classes.multiTable
                )}
                rowClassName={this.getRowClassName}
                overscanRowCount={10}
                {...tableProps}
                onScroll={(ev) => {
                  this.setState({ scrollTop: ev.scrollTop });
                }}
              >
                <Column
                  className={classes.flexContainer}
                  headerRenderer={(headerProps) =>
                    this.headerRenderer({
                      ...headerProps,
                      width: this.columnLeftWidth,
                      columnData: columnsLeft,
                    })
                  }
                  cellRenderer={(cellProps) =>
                    this.cellRenderer({
                      ...cellProps,
                      width: this.columnLeftWidth,
                      columnData: this.columnsLeftToRender,
                    })
                  }
                  dataKey={"left"}
                  width={leftWidth || width - middle}
                  label="Left Table"
                />
                <Column
                  className={clsx(
                    classes.flexContainer,
                    classes.tableColumnRight
                  )}
                  headerRenderer={(headerProps) =>
                    this.headerRenderer({
                      ...headerProps,
                      width: this.columnRightWidth,
                      columnData: columnsRight,
                      maxWidth,
                      minWidth,
                      position: middle,
                    })
                  }
                  cellRenderer={(cellProps) =>
                    this.cellRenderer({
                      ...cellProps,
                      width: this.columnRightWidth,
                      columnData: this.columnsRightToRender,
                    })
                  }
                  dataKey={"right"}
                  width={rightWidth || middle}
                  label="Right Table"
                />
              </Table>
              <ColumnsCustomScroll
                scrollTop={this.state.scrollTop}
                width={middle}
                leftColumnWidth={this.columnLeftWidth}
                rightColumnWidth={this.columnRightWidth}
              />
            </>
          );
        }}
      </AutoSizer>
    );
  }
}

export default withStyles(tableStyles)(MuiVirtualizedMultiTable);
